import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        success:
          "border-green-200 bg-green-50 text-green-700 [a&]:hover:bg-green-100 dark:border-green-800 dark:bg-green-950 dark:text-green-300",
        warning:
          "border-yellow-200 bg-yellow-50 text-yellow-700 [a&]:hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300",
        danger:
          "border-red-200 bg-red-50 text-red-700 [a&]:hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
        info:
          "border-blue-200 bg-blue-50 text-blue-700 [a&]:hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 [a&]:hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300",
        pink:
          "border-pink-200 bg-pink-50 text-pink-700 [a&]:hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 [a&]:hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
